import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAuthToken } from "../../utils/getAuthToken";

// Async thunk to fetch bulk message campaigns for a specific group
export const fetchBulkMessageHistory = createAsyncThunk(
  'bulkMessageHistory/fetchBulkMessageHistory',
  async ({ groupId, page = 1, limit = 10, status }, { rejectWithValue }) => {
    try {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
      if (!token) {
        console.error("❌ No token found for bulk message history");
        return rejectWithValue("Authentication token not found. Please log in again.");
      }
      
      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';
      const params = new URLSearchParams();
      params.append('page', page);
      params.append('limit', limit);
      if (status) params.append('status', status);

      const response = await axios.get(
        `${apiUrl}/api/bulk-messages/campaigns/group/${groupId}?${params}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return {
        campaigns: response.data.data,
        pagination: response.data.pagination,
        groupId
      };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch bulk message history'
      );
    }
  }
);

// Async thunk to resend campaign
export const resendCampaign = createAsyncThunk(
  'bulkMessageHistory/resendCampaign',
  async ({ campaignId }, { rejectWithValue }) => {
    try {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
      if (!token) {
        console.error("❌ No token found for campaign resend");
        return rejectWithValue("Authentication token not found. Please log in again.");
      }
      
      const apiUrl = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:3000';
      
      const response = await axios.post(
        `${apiUrl}/api/bulk-messages/campaigns/${campaignId}/resend`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to resend campaign'
      );
    }
  }
);

const initialState = {
  campaigns: [],
  pagination: {
    currentPage: 1,
    totalPages: 0,
    totalCampaigns: 0,
    hasNext: false,
    hasPrev: false
  },
  loading: false,
  error: null,
  selectedGroupId: null,
  filters: {
    status: null,
    page: 1,
    limit: 10
  }
};

const bulkMessageHistorySlice = createSlice({
  name: 'bulkMessageHistory',
  initialState,
  reducers: {
    clearHistory: (state) => {
      state.campaigns = [];
      state.pagination = initialState.pagination;
      state.error = null;
      state.selectedGroupId = null;
    },
    setSelectedGroupId: (state, action) => {
      state.selectedGroupId = action.payload;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearError: (state) => {
      state.error = null;
    },
    updateCampaignInList: (state, action) => {
      const { campaignId, updates } = action.payload;
      const campaignIndex = state.campaigns.findIndex(campaign => campaign._id === campaignId);
      if (campaignIndex !== -1) {
        state.campaigns[campaignIndex] = { ...state.campaigns[campaignIndex], ...updates };
      }
    },
    updateCampaignProgress: (state, action) => {
      const { campaignId, progress, status } = action.payload;
      const campaignIndex = state.campaigns.findIndex(campaign => campaign._id === campaignId);
      if (campaignIndex !== -1) {
        if (progress) {
          state.campaigns[campaignIndex].progress = {
            ...state.campaigns[campaignIndex].progress,
            ...progress
          };
        }
        if (status) {
          state.campaigns[campaignIndex].status = status;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch bulk message history
      .addCase(fetchBulkMessageHistory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBulkMessageHistory.fulfilled, (state, action) => {
        state.loading = false;
        state.campaigns = action.payload.campaigns;
        state.pagination = action.payload.pagination;
        state.selectedGroupId = action.payload.groupId;
        state.error = null;
      })
      .addCase(fetchBulkMessageHistory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Resend campaign
      .addCase(resendCampaign.pending, (state, action) => {
        // Optional: Add loading state for resending
        const campaignIndex = state.campaigns.findIndex(
          campaign => campaign._id === action.meta.arg.campaignId
        );
        if (campaignIndex !== -1) {
          state.campaigns[campaignIndex].isResending = true;
        }
      })
      .addCase(resendCampaign.fulfilled, (state, action) => {
        const campaignIndex = state.campaigns.findIndex(
          campaign => campaign._id === action.payload._id
        );
        if (campaignIndex !== -1) {
          state.campaigns[campaignIndex] = {
            ...state.campaigns[campaignIndex],
            ...action.payload,
            isResending: false
          };
        }
      })
      .addCase(resendCampaign.rejected, (state, action) => {
        const campaignIndex = state.campaigns.findIndex(
          campaign => campaign._id === action.meta.arg.campaignId
        );
        if (campaignIndex !== -1) {
          state.campaigns[campaignIndex].isResending = false;
        }
        state.error = action.payload;
      });
  }
});

export const {
  clearHistory,
  setSelectedGroupId,
  setFilters,
  clearError,
  updateCampaignInList,
  updateCampaignProgress
} = bulkMessageHistorySlice.actions;

export default bulkMessageHistorySlice.reducer;
